import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import { Check, MessageCircle, TrendingUp, Layout, Smartphone, Mail, MapPin, RefreshCw, Zap, Sparkles, MonitorSmartphone, Target, Search } from 'lucide-react';
import { mockData } from '../mock';

// Import Section Components
import Header from '../components/sections/Header';
import Hero from '../components/sections/Hero';
import TrustBar from '../components/sections/TrustBar';
import SocialProof from '../components/sections/SocialProof';
import WhyChooseSection from '../components/sections/WhyChooseSection';
import WhatsIncludedSection from '../components/sections/WhatsIncludedSection';
import PortfolioSection from '../components/sections/PortfolioSection';
import ProcessSection from '../components/sections/ProcessSection';
import CTASection from '../components/sections/CTASection';
import PricingSection from '../components/sections/PricingSection';
import FAQSection from '../components/sections/FAQSection';
import ContactSection from '../components/sections/ContactSection';
import FinalCTA from '../components/sections/FinalCTA';
import Footer from '../components/sections/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  layout: Layout,
  smartphone: Smartphone,
  mail: Mail,
  'map-pin': MapPin,
  'refresh-cw': RefreshCw,
  zap: Zap,
  sparkles: Sparkles,
  'monitor-smartphone': MonitorSmartphone,
  target: Target,
  search: Search
};

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHeadingHovered, setIsHeadingHovered] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const headingRef = useRef(null);
  
  // Portfolio carousel state
  const [portfolioCarouselIndexes, setPortfolioCarouselIndexes] = useState(
    mockData.portfolio.reduce((acc, project) => {
      acc[project.id] = 0;
      return acc;
    }, {})
  );

  // Logo intro animation effect
  useEffect(() => {
    const introPlayed = sessionStorage.getItem('meshwebIntroPlayed');
    
    if (!introPlayed) {
      setShowIntro(true);
      sessionStorage.setItem('meshwebIntroPlayed', 'true');
      
      setTimeout(() => {
        setShowIntro(false);
        setIntroComplete(true);
      }, 3800);
    } else {
      setIntroComplete(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: response.data.message,
          variant: "default"
        });
        
        setFormData({ name: '', email: '', business: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or email us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      
      if (currentScrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Portfolio carousel handlers
  const handleNextImage = (projectId, imagesLength) => {
    setPortfolioCarouselIndexes(prev => ({
      ...prev,
      [projectId]: (prev[projectId] + 1) % imagesLength
    }));
  };
  
  const handlePrevImage = (projectId, imagesLength) => {
    setPortfolioCarouselIndexes(prev => ({
      ...prev,
      [projectId]: (prev[projectId] - 1 + imagesLength) % imagesLength
    }));
  };
  
  const handleGoToImage = (projectId, index) => {
    setPortfolioCarouselIndexes(prev => ({
      ...prev,
      [projectId]: index
    }));
  };
  
  // Mouse tracking for heading glow effect
  const handleHeadingMouseMove = (e) => {
    if (!headingRef.current) return;
    
    const rect = headingRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setMousePosition({ x, y });
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F5F7FF]">
      <Toaster />
      
      {/* Logo Intro Animation */}
      {showIntro && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0B0F1A] animate-logoIntroFade overflow-hidden">
          {/* Animated background connection bursts */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '0.3s' }}></div>
            <div className="absolute top-1/4 right-1/4 w-3 h-3 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '0.6s' }}></div>
            <div className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '0.9s' }}></div>
            <div className="absolute bottom-1/4 right-1/4 w-3 h-3 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '1.2s' }}></div>
            <div className="absolute top-1/2 left-1/3 w-2 h-2 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-[#D4A574] rounded-full animate-connectionBurst" style={{ animationDelay: '0.8s' }}></div>
          </div>
          
          {/* Main logo with dramatic reveal animation */}
          <div className="relative flex items-center justify-center animate-logoIntroScale">
            <img 
              src="/loading-logo-new.png" 
              alt="MeshWeb" 
              className="h-64 w-64 md:h-80 md:w-80 lg:h-96 lg:w-96 animate-meshReveal animate-meshPulse relative z-10"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      {introComplete && (
        <>
      <Header isScrolled={isScrolled} scrollToContact={scrollToContact} />

      <Hero 
        headingRef={headingRef}
        handleHeadingMouseMove={handleHeadingMouseMove}
        isHeadingHovered={isHeadingHovered}
        setIsHeadingHovered={setIsHeadingHovered}
        mousePosition={mousePosition}
        scrollToContact={scrollToContact}
        scrollToPortfolio={scrollToPortfolio}
        scrollY={scrollY}
      />

      <SocialProof />

      {/* CTA Section - Moved here for better flow */}
      <FinalCTA scrollToContact={scrollToContact} />

      <WhyChooseSection />
      
      <WhatsIncludedSection />

      <PortfolioSection 
        portfolioCarouselIndexes={portfolioCarouselIndexes}
        handleNextImage={handleNextImage}
        handlePrevImage={handlePrevImage}
        handleGoToImage={handleGoToImage}
      />
      
      <ProcessSection />

      <PricingSection scrollToContact={scrollToContact} />

      {/* Testimonials */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0B0F1A] to-[#121826]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-20">
            <Badge className="inline-flex items-center justify-center px-4 py-1.5 bg-gradient-to-r from-[#2F6BFF]/10 to-[#4DA3FF]/10 text-[#2F6BFF] border border-[#2F6BFF]/20 rounded-full text-sm font-medium mb-6 hover:from-[#2F6BFF]/20 hover:to-[#4DA3FF]/20 transition-all duration-300">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Client Feedback</h2>
            <p className="text-[#9AA3B2] text-lg">Hear from local business owners we&apos;ve worked with</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {mockData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-gradient-to-br from-[rgba(20, 25, 32, 0.7)] to-[#151515] border-gray-800 hover:border-[#2F6BFF]/50 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#2F6BFF]/10 transition-all duration-300">
                <CardHeader className="space-y-6">
                  <div className="flex items-start gap-2">
                    <MessageCircle className="h-5 w-5 text-[#2F6BFF] flex-shrink-0 mt-1" />
                    <p className="text-[#F5F7FF] italic leading-relaxed text-lg">&ldquo;{testimonial.text}&rdquo;</p>
                  </div>
                  <div className="pt-4 border-t border-gray-800">
                    <p className="font-semibold text-[#F5F7FF]">{testimonial.name}</p>
                    <p className="text-sm text-[#9AA3B2] mt-1">{testimonial.role}</p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About MeshWeb & How It Works */}
      <section className="py-16 px-6 bg-[#121826]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{mockData.about.title}</h2>
            <p className="text-xl text-[#F5F7FF] max-w-3xl mx-auto mb-4">{mockData.about.description1}</p>
            <p className="text-xl text-[#F5F7FF] max-w-3xl mx-auto">{mockData.about.description2}</p>
          </div>

          <div className="max-w-2xl mx-auto mb-16">
            <div className="h-px bg-gradient-to-r from-transparent via-[#2F6BFF]/30 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0B0F1A] to-[#121826]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="inline-flex items-center justify-center px-4 py-1.5 bg-gradient-to-r from-[#2F6BFF]/10 to-[#4DA3FF]/10 text-[#2F6BFF] border border-[#2F6BFF]/20 rounded-full text-sm font-medium mb-6 hover:from-[#2F6BFF]/20 hover:to-[#4DA3FF]/20 transition-all duration-300">
              Real Results
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Client Success Stories</h2>
            <p className="text-[#9AA3B2] text-lg">See how our websites helped local businesses grow</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.caseStudies.map((study) => (
              <Card key={study.id} className="bg-gradient-to-br from-[rgba(20, 25, 32, 0.7)] to-[#151515] border-gray-800 overflow-hidden hover:border-[#2F6BFF]/50 transition-all duration-300">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={study.image} 
                    alt={study.client}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <CardHeader>
                  <div className="mb-2">
                    <CardTitle className="text-2xl text-[#F5F7FF] mb-2">{study.client}</CardTitle>
                    <Badge variant="outline" className="text-[#2F6BFF] border-[#2F6BFF]/30">
                      {study.industry}
                    </Badge>
                  </div>
                  <div className="space-y-4 mt-6">
                    <div>
                      <p className="text-sm font-semibold text-[#9AA3B2] mb-2">Challenge:</p>
                      <p className="text-[#F5F7FF]">{study.challenge}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#9AA3B2] mb-2">Solution:</p>
                      <p className="text-[#F5F7FF]">{study.solution}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-[#0B0F1A]/50 rounded-lg p-6 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center flex flex-col items-center justify-start">
                          <div className="flex items-center justify-center mb-3 h-8">
                            <TrendingUp className="h-4 w-4 text-[#2F6BFF] flex-shrink-0 mr-2" />
                            <span className="text-2xl sm:text-2xl font-bold text-[#2F6BFF] break-words leading-none">{result.value}</span>
                          </div>
                          <p className="text-xs font-semibold text-[#9AA3B2] mb-2 leading-tight">{result.metric}</p>
                          <p className="text-xs text-[#9AA3B2]">{result.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF]/10 border-l-4 border-[#2F6BFF] p-4 rounded">
                    <p className="text-[#F5F7FF] italic">&ldquo;{study.testimonial}&rdquo;</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <FAQSection />

      <ContactSection 
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      <Footer />

      
      </>
      )}
    </div>
  );
};

export default Home;
